import { Badge } from '@amore/ui/components/badge'
import { Field } from '@amore/ui/components/field'
import { FormLayout } from '@amore/ui/components/form-layout'
import { Input } from '@amore/ui/components/input'
import { Select, SelectItem } from '@amore/ui/components/select'
import { Text } from '@amore/ui/components/typography'
import { supplierCoverImage } from '@amore/ui/supplier-covers'
import { Link } from '@tanstack/react-router'
import { MapPin } from 'lucide-react'
import { useRef, useState } from 'react'
import { newId } from '@/lib/format'
import { supplierStatusLabels, supplierStatusTone } from '@/lib/labels'
import { useUpdate, useWedding } from '@/mock/store'
import { emptyVenue, type VenueSource, venueIdentity } from '@/mock/venues'
import { DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { normalize } from './model'

const custom = 'custom'
const softControl = 'h-12 border-transparent bg-muted/60 hover:bg-muted'

export function AddPlaceSheet({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const data = useWedding()
  const update = useUpdate()
  const { showToast } = useToast()
  const suppliers = data.suppliers.filter(
    (item) =>
      item.category === 'venue' &&
      item.status !== 'cancelled' &&
      !data.venues.some(
        (venue) => venue.source.type === 'supplier' && venue.source.supplierId === item.id,
      ),
  )
  const [choice, setChoice] = useState(suppliers.length ? '' : custom)
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const formRef = useRef<HTMLFormElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const picked = suppliers.find((item) => item.id === choice)
  const preview = picked
    ? venueIdentity(emptyVenue('preview', { type: 'supplier', supplierId: picked.id }), data)
    : undefined
  const region = data.catalog.find((item) => item.id === picked?.catalogServiceId)?.region

  const add = () => {
    if (!choice) {
      setError('Vyberte místo z Mých dodavatelů, nebo zvolte vlastní místo.')
      return
    }
    let source: VenueSource
    if (choice === custom) {
      const duplicate = data.venues.find(
        (venue) => normalize(venueIdentity(venue, data).name) === normalize(name),
      )
      if (!name.trim() || duplicate) {
        setError(name.trim() ? 'Místo s tímto názvem už máte v plánu.' : 'Doplňte název místa.')
        nameRef.current?.focus()
        return
      }
      source = { type: 'custom', name: name.trim(), address: '', private: false }
    } else source = { type: 'supplier', supplierId: choice }
    const venue = emptyVenue(newId('venue'), source)
    update((current) => ({ ...current, venues: [...current.venues, venue] }))
    showToast({
      title: 'Místo přidáno',
      description: venueIdentity(venue, data).name,
      tone: 'success',
    })
    onCreated(venue.id)
  }

  return (
    <DetailSheet
      open
      eyebrow="Nové místo"
      title="Přidat místo"
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      dirty={Boolean(choice && choice !== custom) || Boolean(name.trim())}
      primaryAction={{ label: 'Přidat místo', onClick: () => formRef.current?.requestSubmit() }}
    >
      <FormLayout
        ref={formRef}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          add()
        }}
        className="space-y-6"
      >
        {error ? (
          <p role="alert" className="text-sm text-error-foreground">
            {error}
          </p>
        ) : null}
        <Field
          label="Místo"
          controlId="new-venue"
          description={
            suppliers.length ? (
              'Z Mých dodavatelů, nebo vlastní, třeba zahrada u rodičů.'
            ) : (
              <>
                V Mých dodavatelích zatím žádné místo nemáte.{' '}
                <Link
                  to="/suppliers/catalog"
                  className="font-semibold text-action underline-offset-4 hover:underline"
                >
                  Najít v katalogu
                </Link>
              </>
            )
          }
        >
          <Select
            name="newVenue"
            variant="soft"
            className={softControl}
            placeholder="Vyberte místo"
            value={choice}
            onValueChange={(value) => {
              setError('')
              setChoice(value)
              if (value === custom) requestAnimationFrame(() => nameRef.current?.focus())
            }}
          >
            {suppliers.map((supplier) => (
              <SelectItem
                key={supplier.id}
                value={supplier.id}
                className="min-h-14 [&>span:first-child]:flex-1"
              >
                <span className="flex w-full items-center gap-3">
                  <img
                    src={supplierCoverImage(supplier.category)}
                    alt=""
                    className="size-9 shrink-0 rounded-lg object-cover"
                  />
                  <span className="min-w-0 flex-1 truncate">{supplier.name}</span>
                  <Badge tone={supplierStatusTone(supplier.status)}>
                    {supplierStatusLabels[supplier.status]}
                  </Badge>
                </span>
              </SelectItem>
            ))}
            <SelectItem value={custom} className="min-h-14">
              <span className="flex items-center gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-accent text-action">
                  <MapPin className="size-4" aria-hidden="true" />
                </span>
                Vlastní místo
              </span>
            </SelectItem>
          </Select>
        </Field>
        {picked && preview ? (
          <figure className="space-y-3">
            <img
              src={supplierCoverImage(picked.category)}
              alt=""
              className="aspect-[16/9] w-full rounded-2xl object-cover"
            />
            <figcaption className="space-y-1">
              <p className="font-heading text-xl leading-tight">{picked.name}</p>
              <Text tone="muted">
                {[preview.address, region].filter(Boolean).join(' · ') ||
                  'Adresu doplníte u dodavatele.'}
              </Text>
            </figcaption>
          </figure>
        ) : null}
        {choice === custom ? (
          <Field label="Název místa">
            <Input
              ref={nameRef}
              variant="soft"
              className="h-10 border-transparent bg-muted/60 hover:bg-muted"
              value={name}
              maxLength={160}
              placeholder="Například U rodičů"
              onChange={(event) => {
                setError('')
                setName(event.target.value)
              }}
            />
          </Field>
        ) : null}
        <Text tone="muted">Adresu, využití a domluvy doplníte hned potom.</Text>
      </FormLayout>
    </DetailSheet>
  )
}
